/**
 * ClientMessagesTab — Timeline de comunicacions d'un client.
 *
 * Mostra, agrupat per dia i paginat (plans/whatsapp-capture.md A4.1), amb les mateixes
 * peces que ReservationMessagesTab (`MessageTimeline`):
 *   - CommunicationLog entries (WhatsApp importat, OTA, converses enganxades)
 *   - MessagingTask entries (missatges enviats via el sistema)
 *
 * Inclou botó per obrir PasteConversationDialog i, si algun missatge carregat és d'una conversa
 * de l'inbox, «Obre a l'inbox» (`/whatsapp/<id>`).
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardPaste, Inbox, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCommunicationLogs } from '@/hooks/useCommunicationLogs';
import { useMessagingTasks } from '@/hooks/useMessagingTask';
import { PasteConversationDialog } from './PasteConversationDialog';
import {
  ChannelFilterChips,
  CommunicationLogEntry,
  EmptyTimeline,
  MessageTimeline,
  MessagingTaskEntry,
  TimelineScroll,
  buildTimeline,
  type TimelineItem,
} from './MessageTimeline';
import type { CommunicationLogChannelFilter } from '@guilleumes/types';

export interface ClientMessagesTabProps {
  /** Client ID to fetch communication history for */
  clientId: string;
}

export function ClientMessagesTab({ clientId }: ClientMessagesTabProps) {
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [channel, setChannel] = useState<CommunicationLogChannelFilter | undefined>(undefined);

  const {
    data: logPages,
    isLoading: logsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCommunicationLogs(clientId, { channel });
  const { data: allTasks, isLoading: tasksLoading } = useMessagingTasks();

  const isLoading = logsLoading || tasksLoading;
  // Conversa de l'inbox del missatge més recent carregat que en tingui.
  const inboxConversationId =
    logPages?.pages.flatMap((page) => page.items).find((log) => log.whatsappConversationId)
      ?.whatsappConversationId ?? null;

  // Filter messaging tasks for this client and build unified timeline
  const timeline = useMemo<TimelineItem[]>(() => {
    const logItems: TimelineItem[] = (logPages?.pages ?? []).flatMap((page) =>
      page.items.map((log) => ({
        id: `log-${log.id}`,
        date: new Date(log.loggedAt),
        node: <CommunicationLogEntry log={log} />,
      })),
    );

    const others: TimelineItem[] = [];
    // Les tasques són missatges de WhatsApp: surten a «Tots» i a «WhatsApp».
    if (allTasks && (!channel || channel === 'WHATSAPP')) {
      for (const task of allTasks.filter((t) => t.clientId === clientId)) {
        others.push({
          id: `task-${task.id}`,
          date: new Date(task.sentAt || task.createdAt),
          node: <MessagingTaskEntry task={task} />,
        });
      }
    }

    return buildTimeline(logItems, others, !!hasNextPage);
  }, [logPages, allTasks, clientId, channel, hasNextPage]);

  return (
    <div className="min-w-0 space-y-4">
      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Historial de comunicacions
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {inboxConversationId && (
            <Button asChild size="sm" variant="outline" className="gap-2">
              <Link to={`/whatsapp/${inboxConversationId}`}>
                <Inbox className="h-4 w-4" />
                Obre a l&apos;inbox
              </Link>
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPasteDialogOpen(true)}
            className="gap-2"
          >
            <ClipboardPaste className="h-4 w-4" />
            Enganxar conversa WA
          </Button>
        </div>
      </div>

      <ChannelFilterChips value={channel} onChange={setChannel} />

      <Separator />

      {/* Timeline */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Carregant historial...</span>
        </div>
      ) : timeline.length === 0 ? (
        <EmptyTimeline channel={channel} owner="client" className="py-12" />
      ) : (
        <TimelineScroll className="max-h-[60vh]">
          <MessageTimeline
            className="pr-2"
            items={timeline}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        </TimelineScroll>
      )}

      {/* Paste dialog */}
      <PasteConversationDialog
        clientId={clientId}
        open={pasteDialogOpen}
        onOpenChange={setPasteDialogOpen}
      />
    </div>
  );
}
